import SearchInput from 'components/inputs/SearchInput';
import Tooltip from 'components/Tooltip';
import TooltipButton from 'components/Tooltip/TooltipButton';
import TooltipHover from 'components/Tooltip/TooltipHover';
import { getAccountShortName } from 'constants/integrations';
import { fileBrowser } from 'copy/copy';
import syncIcon from 'images/icons/sync.svg';
import React from 'react';
import TimeAgo from 'react-timeago';
import { useTranslator } from '@bluebeam/react-i18n';

interface Props {
  selectedCount: number;
  onSync?: () => void;
  recommendSyncProviderType?: string;
  disableSync?: boolean;
  searchInput?: string;
  onSearchChange?: (searchInput: string) => void;
  className?: string;
  countLabel?: string;
  lastSyncDate?: string;
}

const SyncTooltip: React.FC<{
  providerType: string;
  lastSyncDate: string;
}> = ({ providerType, children, lastSyncDate }) => {
  const { translate } = useTranslator(['integrationsPage']);

  const providerName =
    getAccountShortName(providerType) === 'Account'
      ? translate('integrationsPage:accountDefaultShortName')
      : getAccountShortName(providerType);
  return (
    <Tooltip
      title={fileBrowser.bar.recommendSync.title}
      content={
        <div>
          <div className="mb3">
            {fileBrowser.bar.recommendSync.problem(providerName)}{' '}
            <TimeAgo date={lastSyncDate} />.
          </div>
          <div>{fileBrowser.bar.recommendSync.solution}</div>
        </div>
      }
    >
      <TooltipHover>{children}</TooltipHover>
    </Tooltip>
  );
};

const SyncButton: React.FC<{
  onSync: () => void;
  disableSync?: boolean;
}> = ({ onSync, disableSync }) => {
  const { translate } = useTranslator(['buttons']);

  return (
    <button
      id="integrations-modal-sync-btn"
      className="row items-center btn-sm btn-link"
      onClick={onSync}
      disabled={disableSync}
    >
      <img src={`${syncIcon}`} alt="Sync with Account" />
      {translate('buttons:sync')}
    </button>
  );
};

const FileBrowserBar: React.FC<Props> = (props: Props) => {
  const {
    selectedCount,
    onSync,
    recommendSyncProviderType,
    disableSync,
    onSearchChange,
    searchInput,
    className,
    countLabel,
    lastSyncDate,
  } = props;

  const { translate } = useTranslator(['integrationsPage']);

  const label =
    countLabel !== undefined
      ? countLabel
      : translate('integrationsPage:sharedItems');

  return (
    <div className={`row justify-between items-center ${className}`}>
      <div className="medium font-size-5">{`${label} (${selectedCount})`}</div>
      <div className="row justify-between items-center">
        <div className="mr5">
          {onSync && !recommendSyncProviderType && (
            <SyncButton onSync={onSync} disableSync={disableSync} />
          )}
          {onSync && recommendSyncProviderType && lastSyncDate && (
            <SyncTooltip
              providerType={recommendSyncProviderType}
              lastSyncDate={lastSyncDate}
            >
              <SyncButton onSync={onSync} disableSync={disableSync} />
              <div
                className="pos-a"
                css={{
                  marginLeft: '100px',
                  marginTop: '-39px',
                }}
              >
                <TooltipButton variant="alert" />
              </div>
            </SyncTooltip>
          )}
        </div>
        {onSearchChange && (
          <SearchInput
            id="integrations-results-search"
            value={searchInput}
            onChange={onSearchChange}
            disableDebounce
          />
        )}
      </div>
    </div>
  );
};

export default FileBrowserBar;
